import { useEffect, useMemo, useRef, useState } from 'react';
import { api, qs } from '../lib/api';
import { finDia, hora, inicioDia, inicioSemana, mismoDia, sumaDias } from '../lib/formato';
import { useSesion } from '../lib/sesion';
import type { EventoAgenda } from '../lib/tipos';
import { Aviso, Boton, Campo } from './ui';
import { AVISO_OCUPADO, franjaDisponible, seleccionVacia, siguienteSeleccion, rangoEntre, type CeldaFranja, type EstadoSeleccion } from '../lib/seleccionFranja';
import type { PointerEvent as ReactPointerEvent } from 'react';

/* ═══════════════════════════ Mejora M-02 ═══════════════════════════════════
   Selector de franja.

   Tres cosas que antes no hacía:

   1. Se elige sobre una rejilla horaria, no escribiendo dos fechas a mano.
   2. La ocupación se ve MIENTRAS se elige, superpuesta en la propia rejilla.
      Para el Comercial, todo lo ajeno es una banda gris sin texto: la
      anonimización ya viene aplicada del servidor y aquí no se deshace.
   3. Al fijar el día y la hora de inicio, el día de fin se hereda solo y la
      hora de fin se propone. No hay que volver a buscar la fecha en «hasta».
   ═════════════════════════════════════════════════════════════════════════ */

export interface Franja {
  /** Formato de <input type="datetime-local">: 'YYYY-MM-DDTHH:mm'. */
  inicio: string;
  fin: string;
}

const DOW = ['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom'];
const DIA_INICIO = 8; // primera hora visible
const DIA_FIN = 21; // última hora visible

/* ── Utilidades de fecha en el formato del <input datetime-local> ───────── */

const dosCifras = (n: number) => String(n).padStart(2, '0');

function aTexto(d: Date) {
  return `${d.getFullYear()}-${dosCifras(d.getMonth() + 1)}-${dosCifras(d.getDate())}T${dosCifras(
    d.getHours(),
  )}:${dosCifras(d.getMinutes())}`;
}

const aFecha = (v: string) => (v ? new Date(v) : null);
const soloDia = (v: string) => v.slice(0, 10);
const soloHora = (v: string) => v.slice(11, 16);
const unir = (dia: string, hhmm: string) => `${dia}T${hhmm}`;

export function SelectorFranja({
  valor,
  onCambio,
  pasoMin = 15,
  bloquearOcupadas = false,
  excludeRequestId,
  excludeUnavailabilityId,
  ayuda,
}: {
  valor: Franja | null;
  onCambio: (v: Franja | null) => void;
  bloquearOcupadas?: boolean;
  excludeRequestId?: string;
  excludeUnavailabilityId?: string;
  /** Altura de una celda de la rejilla, en minutos. */
  pasoMin?: number;
  ayuda?: string;
}) {
  const { es } = useSesion();
  const anonimo = es('SALESPERSON'); // el Comercial no ve detalle ajeno
  const [ancla, setAncla] = useState(() => (valor ? new Date(valor.inicio) : new Date()));
  const [eventos, setEventos] = useState<EventoAgenda[]>([]);
  const [otroDia, setOtroDia] = useState(
    () => !!valor && soloDia(valor.inicio) !== soloDia(valor.fin),
  );
  const [seleccion, setSeleccion] = useState<EstadoSeleccion>(seleccionVacia);
  const [previsual, setPrevisual] = useState<Franja | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);
  const gesto = useRef<{ id: number; inicio: CeldaFranja; ultima: CeldaFranja; movido: boolean } | null>(null);
  useEffect(() => {
    // Cambios externos (otra propuesta o alternativa) empiezan una nueva secuencia.
    if (valor?.inicio !== seleccion.franja?.inicio || valor?.fin !== seleccion.franja?.fin) {
      setSeleccion(valor ? { fase: 'fijada', franja: valor } : seleccionVacia());
    }
  }, [valor?.inicio, valor?.fin]);
  const estadoRef = useRef(seleccion);
  estadoRef.current = seleccion;
  const eventosOrdenados = useMemo(() => [...eventos].sort((a, b) => Number(a.type === 'PROPOSED') - Number(b.type === 'PROPOSED')), [eventos]);
  const ocupadas = eventos.filter(e => e.type !== 'PROPOSED' &&
    (!excludeRequestId || e.requestId !== excludeRequestId) &&
    (!excludeUnavailabilityId || e.unavailabilityId !== excludeUnavailabilityId))
    .map(e => ({ inicio: e.start, fin: e.end }));
  function cancelar() {
    gesto.current = null; setPrevisual(null);
    setSeleccion(seleccionVacia()); setAviso(null); onCambio(null);
  }
  useEffect(() => {
    const esc = (e: KeyboardEvent) => { if (e.key === 'Escape') cancelar(); };
    window.addEventListener('keydown', esc);
    return () => window.removeEventListener('keydown', esc);
  }, [onCambio]);

  const lunes = useMemo(() => inicioSemana(ancla), [ancla]);
  const dias = useMemo(() => Array.from({ length: 6 }, (_, i) => sumaDias(lunes, i)), [lunes]);

  /* Ocupación de la semana visible. Si falla, la rejilla sigue usable. */
  useEffect(() => {
    let vivo = true;
    const desde = inicioDia(lunes);
    const hasta = finDia(sumaDias(lunes, 6));
    api
      .get<{ events: EventoAgenda[] }>(
        `/calendar/events${qs({ from: desde.toISOString(), to: hasta.toISOString() })}`,
      )
      .then((r) => vivo && setEventos(r.events))
      .catch(() => vivo && setEventos([]));
    return () => {
      vivo = false;
    };
  }, [lunes.getTime()]);

  const visible = previsual ?? valor;
  const inicio = visible ? aFecha(visible.inicio) : null;
  const fin = visible ? aFecha(visible.fin) : null;

  /* ── Selección sobre la rejilla ─────────────────────────────────────── */

  function seleccionar(celda: CeldaFranja, arrastre?: CeldaFranja) {
    const r = siguienteSeleccion(estadoRef.current, celda, {
      ahora: Date.now(), ocupadas: bloquearOcupadas ? ocupadas : [], arrastre,
    });
    setAviso(r.aviso);
    if (r.estado !== estadoRef.current) { setSeleccion(r.estado); onCambio(r.cambio); setOtroDia(false); }
  }
  function celdaEn(e: ReactPointerEvent): CeldaFranja | null {
    const button = document.elementsFromPoint(e.clientX, e.clientY)
      .map((element) => element.closest<HTMLElement>('[data-celda]'))
      .find((element): element is HTMLElement => !!element);
    const t = button?.dataset.celda;
    return t ? { inicio: t, fin: aTexto(new Date(new Date(t).getTime() + pasoMin * 60000)) } : null;
  }
  function mover(e: ReactPointerEvent) {
    const g = gesto.current;
    if (!g || g.id !== e.pointerId) return;
    const c = celdaEn(e);
    if (c && c.inicio.slice(0, 10) === g.inicio.inicio.slice(0, 10)) {
      if (c.inicio !== g.inicio.inicio) g.movido = true;
      g.ultima = c;
      if (g.movido) {
        const f = rangoEntre(g.inicio, c);
        setPrevisual(franjaDisponible(f, bloquearOcupadas ? ocupadas : [], Date.now()) ? f : null);
      }
    }
  }
  function terminar(e: ReactPointerEvent) {
    mover(e);
    const g = gesto.current;
    if (!g || g.id !== e.pointerId) return;
    gesto.current = null; setPrevisual(null);
    seleccionar(g.ultima, g.movido ? g.inicio : undefined);
  }
  function cambiarFino(f: Franja) {
    if (!franjaDisponible(f, bloquearOcupadas ? ocupadas : [], Date.now())) { setAviso(AVISO_OCUPADO); return; }
    setAviso(null); setSeleccion({ fase: 'fijada', franja: f }); onCambio(f);
  }

  /* ── Campos finos. El día de fin sigue al de inicio salvo que se pida
       expresamente lo contrario. ─────────────────────────────────────── */

  function cambiarDia(dia: string) {
    if (!valor) return;
    cambiarFino({
      inicio: unir(dia, soloHora(valor.inicio)),
      // Aquí está el arreglo que pedías: el «hasta» hereda el día.
      fin: unir(otroDia ? soloDia(valor.fin) : dia, soloHora(valor.fin)),
    });
  }

  function cambiarHoraInicio(hhmm: string) {
    if (!valor) return;
    const nuevoInicio = unir(soloDia(valor.inicio), hhmm);
    const dInicio = new Date(nuevoInicio);
    const dFin = new Date(valor.fin);
    // Conserva la duración elegida; los clics de rejilla nunca inventan una.
    const duracion = Math.max(pasoMin * 60000, dFin.getTime() - new Date(valor.inicio).getTime());
    const nuevoFin = dFin.getTime() > dInicio.getTime()
      ? valor.fin
      : aTexto(new Date(dInicio.getTime() + duracion));
    cambiarFino({ inicio: nuevoInicio, fin: nuevoFin });
  }

  function cambiarHoraFin(hhmm: string) {
    if (!valor) return;
    cambiarFino({ inicio: valor.inicio, fin: unir(soloDia(valor.fin), hhmm) });
  }

  /* ── Rejilla ────────────────────────────────────────────────────────── */

  const celdasPorHora = 60 / pasoMin;
  const filas = (DIA_FIN - DIA_INICIO) * celdasPorHora;
  const ahora = new Date();

  /** Evento que ocupa una celda, si lo hay. */
  function ocupacion(celda: Date) {
    const finCelda = new Date(celda.getTime() + pasoMin * 60000);
    return eventosOrdenados.find((e) => {
      if ((excludeRequestId && e.requestId === excludeRequestId) ||
        (excludeUnavailabilityId && e.unavailabilityId === excludeUnavailabilityId)) return false;
      const a = new Date(e.start).getTime();
      const b = new Date(e.end).getTime();
      return a < finCelda.getTime() && b > celda.getTime();
    });
  }

  const seleccionada = (celda: Date): 'inicio' | 'fin' | 'true' | undefined => {
    if (seleccion.fase === 'fin' && seleccion.ancla?.inicio === aTexto(celda)) return 'inicio';
    if (!inicio || !fin) return undefined;
    const t = celda.getTime();
    const finCelda = t + pasoMin * 60000;
    if (finCelda <= inicio.getTime() || t >= fin.getTime()) return undefined;
    if (t <= inicio.getTime()) return 'inicio';
    if (finCelda >= fin.getTime()) return 'fin';
    return 'true';
  };

  return (
    <div className="selector-franja" style={{ ['--sf-dias' as string]: String(dias.length) }}>
      <div className="sf-barra">
        <Boton variante="plano" tam="s" onClick={() => setAncla(sumaDias(lunes, -7))}>
          ‹ Semana anterior
        </Boton>
        <span className="rotulo">
          {lunes.toLocaleDateString('es-ES', { day: 'numeric', month: 'long' })} –{' '}
          {sumaDias(lunes, 5).toLocaleDateString('es-ES', {
            day: 'numeric',
            month: 'long',
            year: 'numeric',
          })}
        </span>
        <Boton variante="plano" tam="s" onClick={() => setAncla(sumaDias(lunes, 7))}>
          Semana siguiente ›
        </Boton>
      </div>

      <div
        className="sf-rejilla"
        onPointerMove={mover}
        onPointerUp={terminar}
        onPointerCancel={() => { gesto.current = null; setPrevisual(null); }}
      >
        <div className="sf-cabecera" />
        {dias.map((d) => (
          <div
            key={d.toISOString()}
            className="sf-cabecera"
            data-hoy={mismoDia(d, ahora) ? 'true' : undefined}
            data-pasado={d < inicioDia(ahora) ? 'true' : undefined}
          >
            <span className="dow">{DOW[(d.getDay() + 6) % 7]}</span>
            <span className="num">{d.getDate()}</span>
          </div>
        ))}

        {Array.from({ length: filas }, (_, fila) => {
          const minutos = DIA_INICIO * 60 + fila * pasoMin;
          const enPunto = minutos % 60 === 0;
          return (
            <div key={fila} style={{ display: 'contents' }}>
              <div className="sf-hora">
                {enPunto ? `${dosCifras(Math.floor(minutos / 60))}:00` : ''}
              </div>
              {dias.map((d) => {
                const celda = new Date(d);
                celda.setHours(0, minutos, 0, 0);
                const pasado = celda < ahora;
                const ev = ocupacion(celda);
                const sel = seleccionada(celda);
                // La etiqueta solo se escribe en la primera celda del bloque.
                const primeraDelBloque =
                  ev && new Date(ev.start).getTime() >= celda.getTime() - pasoMin * 60000 + 1;
                return (
                  <button
                    key={celda.toISOString()}
                    type="button"
                    className="sf-celda"
                    data-celda={aTexto(celda)}
                    data-enpunto={enPunto ? 'true' : undefined}
                    data-pasado={pasado ? 'true' : undefined}
                    data-ocupado={ev?.type === 'UNAVAILABILITY' ? 'UNAVAILABLE' : ev?.type}
                    data-sel={sel}
                    disabled={pasado || (bloquearOcupadas && !!ev && ev.type !== 'PROPOSED')}
                    aria-label={`${d.toLocaleDateString('es-ES')} ${dosCifras(
                      Math.floor(minutos / 60),
                    )}:${dosCifras(minutos % 60)}${ev ? ` · ${ev.title}` : ''}`}
                    onPointerDown={(e) => {
                      if (e.button !== 0 || !e.isPrimary) return;
                      const c = { inicio: aTexto(celda), fin: aTexto(new Date(celda.getTime() + pasoMin * 60000)) };
                      gesto.current = { id: e.pointerId, inicio: c, ultima: c, movido: false };
                      e.currentTarget.setPointerCapture(e.pointerId);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault(); seleccionar({ inicio: aTexto(celda), fin: aTexto(new Date(celda.getTime() + pasoMin * 60000)) });
                      }
                    }}
                  >
                    {primeraDelBloque && (
                      <span className="sf-etiqueta">
                        {anonimo && ev!.type === 'BLOCKED' ? 'Bloqueado' : ev!.title}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          );
        })}
      </div>

      <div className="sf-leyenda">
        <span>
          <i style={{ background: 'var(--marca)' }} /> Su selección
        </span>
        <span>
          <i style={{ background: 'var(--marca-clara)', border: '1px solid var(--marca-media)' }} />{' '}
          {anonimo ? 'Ocupado' : 'Visita autorizada'}
        </span>
        {!anonimo && (
          <span>
            <i style={{ background: 'var(--ambar-suave)', border: '1px solid var(--ambar)' }} /> Cocina no
            disponible
          </span>
        )}
        <span>
          <i
            style={{
              background:
                'repeating-linear-gradient(-45deg,var(--papel),var(--papel) 3px,var(--acero-suave) 3px,var(--acero-suave) 6px)',
            }}
          />{' '}
          Bloqueado
        </span>
      </div>

      {aviso && <Aviso tono="aviso">{aviso}</Aviso>}
      {seleccion.fase === 'fin' && <div className="fila" role="status">Inicio {soloHora(seleccion.ancla!.inicio)} · pulse la hora de fin <Boton variante="plano" tam="s" onClick={cancelar}>Cancelar selección</Boton></div>}
      <div className="sf-resumen">
        {inicio && fin ? (
          <span className="elegido">
            {inicio.toLocaleDateString('es-ES', {
              weekday: 'long',
              day: 'numeric',
              month: 'long',
            })}
            {' · '}
            {hora(inicio)}–{hora(fin)}
          </span>
        ) : (
          <span className="vacio-sel">
            Pulse una hora en la rejilla, o arrastre para marcar el rango completo.
          </span>
        )}
      </div>

      {valor && (
        <>
          <div className="sf-finos">
            <Campo etiqueta="Día">
              <input
                type="date"
                value={soloDia(valor.inicio)}
                onChange={(e) => cambiarDia(e.target.value)}
              />
            </Campo>
            <Campo etiqueta="Desde">
              <input
                type="time"
                step={900}
                value={soloHora(valor.inicio)}
                onChange={(e) => cambiarHoraInicio(e.target.value)}
              />
            </Campo>
            <Campo etiqueta="Hasta">
              <input
                type="time"
                step={900}
                value={soloHora(valor.fin)}
                onChange={(e) => cambiarHoraFin(e.target.value)}
              />
            </Campo>
            {otroDia && (
              <Campo etiqueta="Día de fin">
                <input
                  type="date"
                  value={soloDia(valor.fin)}
                  onChange={(e) =>
                    cambiarFino({ inicio: valor.inicio, fin: unir(e.target.value, soloHora(valor.fin)) })
                  }
                />
              </Campo>
            )}
            <Boton
              variante="plano"
              tam="s"
              onClick={() => {
                const siguiente = !otroDia;
                setOtroDia(siguiente);
                if (!siguiente) {
                  cambiarFino({
                    inicio: valor.inicio,
                    fin: unir(soloDia(valor.inicio), soloHora(valor.fin)),
                  });
                }
              }}
            >
              {otroDia ? 'Termina el mismo día' : '¿Termina otro día?'}
            </Boton>
          </div>
          {ayuda && (
            <p className="sutil" style={{ margin: '0 var(--r4) var(--r4)' }}>
              {ayuda}
            </p>
          )}
        </>
      )}
    </div>
  );
}

/** Punto de partida razonable: el próximo día laborable a las 10:00. */
export function franjaPorDefecto(duracionMin = 120): Franja {
  const d = new Date();
  d.setDate(d.getDate() + 7);
  d.setHours(10, 0, 0, 0);
  return { inicio: aTexto(d), fin: aTexto(new Date(d.getTime() + duracionMin * 60000)) };
}

export { aTexto as franjaATexto };
